import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const AssignmentModal = ({ isOpen, onClose, onSave, assignment, mode, drivers = [], units = [], yards = [], saveError = null }) => {
  const [formData, setFormData] = useState({
    assignmentType: 'driver-unit',
    driverId: '',
    unitId: '',
    yardId: '',
    roleType: 'primary',
    startDate: '',
    endDate: '',
    status: 'pending'
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (assignment && mode === 'edit') {
      setFormData({
        assignmentType: assignment?.assignmentType || 'driver-unit',
        driverId: assignment?.driverId || '',
        unitId: assignment?.unitId || '',
        yardId: assignment?.yardId || '',
        roleType: assignment?.roleType || 'primary',
        startDate: assignment?.startDate || '',
        endDate: assignment?.endDate || '',
        status: assignment?.status || 'pending'
      });
    } else {
      setFormData({
        assignmentType: 'driver-unit',
        driverId: '',
        unitId: '',
        yardId: '',
        roleType: 'primary',
        startDate: '',
        endDate: '',
        status: 'pending'
      });
    }
    setErrors({});
  }, [assignment, mode, isOpen]);

  const assignmentTypeOptions = [
    { value: 'driver-unit', label: 'Driver-Unit Assignment' },
    { value: 'unit-yard', label: 'Unit-Yard Assignment' }
  ];

  // Build driver options from passed-in drivers (active drivers from the drivers table)
  const driverOptions = drivers?.length > 0
    ? drivers
        ?.filter(d => d?.isActive !== false)
        ?.map(d => ({ value: String(d?.id || d?.driverId), label: d?.name }))
    : [];

  // Build unit options from passed-in units
  const unitOptions = units?.length > 0
    ? units?.map(u => ({ value: String(u?.id), label: `${u?.unitNumber}${u?.make ? ' - ' + u?.make : ''}` }))
    : [];

  const yardOptions = yards?.length > 0
    ? yards?.map(y => ({ value: String(y?.id), label: y?.name }))
    : [];

  const roleTypeOptions = [
    { value: 'primary', label: 'Primary Driver' },
    { value: 'relief', label: 'Relief Driver' }
  ];

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'active', label: 'Active' },
    { value: 'upcoming', label: 'Upcoming' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (formData?.assignmentType === 'driver-unit') {
      if (!formData?.driverId) newErrors.driverId = 'Driver is required';
      if (!formData?.unitId) newErrors.unitId = 'Unit is required';
    } else {
      if (!formData?.unitId) newErrors.unitId = 'Unit is required';
      if (!formData?.yardId) newErrors.yardId = 'Yard is required';
    }

    // Start date is optional — no validation required

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSave(formData);
      // Do NOT call onClose() here — parent closes modal only on success
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[300]" onClick={onClose}></div>
      <div className="fixed inset-0 z-[310] flex items-center justify-center p-4">
        <div className="bg-card border border-border rounded-lg shadow-elevation-3 w-full max-w-2xl max-h-[90vh] overflow-hidden animate-slide-in">
          <div className="p-6 border-b border-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Icon name="Plus" size={24} color="var(--color-primary)" strokeWidth={2} />
                <h2 className="text-xl font-semibold text-foreground">
                  {mode === 'edit' ? 'Edit Assignment' : 'Create New Assignment'}
                </h2>
              </div>
              <Button variant="ghost" size="sm" iconName="X" onClick={onClose} />
            </div>
          </div>

          {saveError && (
            <div className="mx-6 mt-4 p-3 bg-destructive/10 border border-destructive/30 rounded-md flex items-start space-x-2">
              <Icon name="AlertCircle" size={16} color="var(--color-destructive)" strokeWidth={2} className="mt-0.5 flex-shrink-0" />
              <p className="text-sm text-destructive">{saveError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-10rem)]">
            <Select
              label="Assignment Type"
              description="Select the type of assignment to create"
              options={assignmentTypeOptions}
              value={formData?.assignmentType}
              onChange={(value) => handleChange('assignmentType', value)}
              required
            />

            {formData?.assignmentType === 'driver-unit' && (
              <>
                <Select
                  label="Driver"
                  description="Select the driver for this assignment"
                  options={driverOptions}
                  value={formData?.driverId}
                  onChange={(value) => handleChange('driverId', value)}
                  error={errors?.driverId}
                  searchable
                  required
                />

                <Select
                  label="Role Type"
                  description="Specify the driver's role in this assignment"
                  options={roleTypeOptions}
                  value={formData?.roleType}
                  onChange={(value) => handleChange('roleType', value)}
                  required
                />
              </>
            )}

            <Select
              label="Unit"
              description="Select the unit for this assignment"
              options={unitOptions}
              value={formData?.unitId}
              onChange={(value) => handleChange('unitId', value)}
              error={errors?.unitId}
              searchable
              required
            />

            {formData?.assignmentType === 'unit-yard' && (
              <Select
                label="Yard Location"
                description="Select the yard where the unit will be assigned"
                options={yardOptions}
                value={formData?.yardId}
                onChange={(value) => handleChange('yardId', value)}
                error={errors?.yardId}
                searchable
                required
              />
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                type="date"
                label="Start Date (optional)"
                description="Assignment start date"
                value={formData?.startDate}
                onChange={(e) => handleChange('startDate', e?.target?.value)}
                error={errors?.startDate}
              />

              <Input
                type="date"
                label="End Date"
                description="Leave empty for ongoing assignment"
                value={formData?.endDate}
                onChange={(e) => handleChange('endDate', e?.target?.value)}
              />
            </div>

            <Select
              label="Status"
              description="Set the initial status of the assignment"
              options={statusOptions}
              value={formData?.status}
              onChange={(value) => handleChange('status', value)}
              required
            />
          </form>

          <div className="p-6 border-t border-border bg-muted/30">
            <div className="flex items-center justify-end space-x-3">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button variant="default" iconName="Check" onClick={handleSubmit}>
                {mode === 'edit' ? 'Update Assignment' : 'Create Assignment'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AssignmentModal;